// JOBE-0160.2: W1 Attention list — drill-context plumbing and kind discrimination.
//
// Renders an ordered stack of <AttentionRow /> elements.
// Kind dispatch table:
//   decide  → /agent/{agentName}?focus=pending-decision
//   approve → /projects?status=approvals-blocked&agent={agentName}
//   reply   → /agent/{agentName}?focus=pending-reply
//   signal  → /organization/{dept}[?agent={agentName}]
//
// Each row's "Open" affordance fires withDrillContext() so the J1 return-loop
// can scroll to the originating row when the operator returns from a fix.
//
// Data source — 2026-05-24:
//   When wcc.mock.attention is ON, the fixture is rendered as-is.
//   When OFF (default), the list is synthesized from useOrganizationPeople():
//   every agent NOT in the 'Available' phase becomes a `signal`-kind row.
//   The other kinds (decide/approve/reply) need event sources that don't
//   exist on the wire yet, so they're omitted in the derived branch.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { flaggedFetch } from '../lib/flags.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { AttentionRow } from '../components/AttentionRow/AttentionRow.jsx';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { useOrganizationPeople } from '../organization/fetchOrganizationPeople.js';
import './AttentionList.css';

// Map fixture urgency to AttentionRow severity.
const URGENCY_TO_SEVERITY = {
  high:   'high',
  medium: 'med',
  low:    'low',
};

// Build a deterministic rowId when the fixture row has no explicit id.
function makeRowId(row) {
  if (row.id) return row.id;
  if (row.agentName) return `${row.kind}-${row.agentName}`;
  return `${row.kind}-${(row.dept || 'unknown').toLowerCase().replace(/\s+/g, '-')}`;
}

// Build drill target URL per kind.
function buildDrillTarget(row) {
  switch (row.kind) {
    case 'decide':
      return `/agent/${row.agentName}?focus=pending-decision`;
    case 'approve':
      return `/projects?status=approvals-blocked&agent=${row.agentName}`;
    case 'reply':
      return `/agent/${row.agentName}?focus=pending-reply`;
    case 'signal': {
      const deptSlug = (row.dept || 'unknown').toLowerCase().replace(/\s+/g, '-');
      const base = `/organization/${deptSlug}`;
      return row.agentName ? `${base}?agent=${row.agentName}` : base;
    }
    default:
      return '/';
  }
}

// Build the drill context object for a row.
function buildDrillContext(row, source) {
  const expandedFilterMap = {
    decide:  'pending-decision',
    approve: 'approvals-blocked',
    reply:   'pending-reply',
    signal:  null,
  };
  return {
    source,
    rowId: makeRowId(row),
    kind: row.kind,
    expandedFilter: expandedFilterMap[row.kind] ?? null,
  };
}

// AttentionRowWrapper adds hover-lift state tracking and wires drill context.
function AttentionRowWrapper({ row, onOpen }) {
  const [hovered, setHovered] = useState(false);

  const severity = URGENCY_TO_SEVERITY[row.urgency] || 'low';
  const what = (
    <>
      <PillChip label={row.kind} />
      {' '}
      {row.kind === 'signal' ? row.text : row.title}
    </>
  );
  const since = row.meta && row.meta[0] ? row.meta[0] : '';

  return (
    <div
      className={['attention-row-wrapper', hovered ? 'attention-row-wrapper--hover' : ''].filter(Boolean).join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <AttentionRow
        severity={severity}
        who={row.agentName ? capitalize(row.agentName) : (row.dept || '')}
        dept={row.dept || ''}
        what={what}
        since={since}
        onOpen={() => onOpen(row)}
      />
    </div>
  );
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Derive attention rows from the organization directory.
// Every agent NOT in 'Available' phase produces a 'signal' attention row.
function deriveRowsFromOrg(orgRows) {
  const agents = orgRows.filter((r) => r.type === 'agent');
  const unhealthy = agents.filter((a) => a.phase !== 'Available');
  return unhealthy.map((agent) => {
    const phaseLabel = agent.phase || 'unknown';
    const urgency =
      agent.phase === 'Disabled' || agent.phase == null
        ? 'high'
        : agent.phase === 'Degraded'
          ? 'medium'
          : 'low';
    return {
      kind: 'signal',
      agentName: agent.id,
      dept: agent.dept,
      text: `${agent.displayName} · ${phaseLabel}`,
      urgency,
      meta: [],
    };
  });
}

export function AttentionList() {
  const navigate = useNavigate();
  const overrides = useFlagStore((s) => s.overrides);
  const mockRows = flaggedFetch('wcc.mock.attention', overrides);
  const { rows: orgRows } = useOrganizationPeople();

  // Mock-flag ON → fixture (legacy demo). OFF → derive signals from org.
  const isMock = Array.isArray(mockRows);
  const rows = isMock ? mockRows : deriveRowsFromOrg(orgRows);
  const source = isMock ? 'wcc.mock.attention' : 'wcc-attention';

  const handleOpen = (row) => {
    const targetUrl = buildDrillTarget(row);
    const ctx = buildDrillContext(row, source);
    navigate(withDrillContext(targetUrl, ctx));
  };

  if (!rows.length) {
    // Derived empty state: all agents are Available. Use the design-system
    // empty-state treatment (italic serif + --ink-soft via .attention-empty).
    const emptyMsg = isMock
      ? 'No open items — all quiet.'
      : 'All agents available · no attention needed.';
    return (
      <div className="attention-list">
        <p className="attention-empty">{emptyMsg}</p>
      </div>
    );
  }

  return (
    <div className="attention-list">
      {rows.map((row) => (
        <AttentionRowWrapper key={makeRowId(row)} row={row} onOpen={handleOpen} />
      ))}
    </div>
  );
}
